import { Theme } from '@afterpaytouch/core'

const parseConfig = (input, fallback) => {
  switch (typeof input) {
    case 'string':
      if (isNaN(Number(input)) || input.trim().length === 0) {
        return fallback
      }
      return Number(input)
    case 'number':
      return input
    default:
      return fallback
  }
}

// On client, read from window.__ENV__ injected by _document.tsx
// On server (SSR), read from process.env
const getEnv = (key, fallback = '') => {
  if (typeof window !== 'undefined' && window.__ENV__) {
    return window.__ENV__[key] ?? fallback
  }
  return process.env[key] ?? fallback
}

export const api = {
  PORTAL_API_BASE_URL: getEnv('PORTAL_API_BASE_URL'),
  PORTAL_API_GET_CHECKOUT_PATH_SUFFIX: getEnv('PORTAL_API_GET_CHECKOUT_PATH_SUFFIX'),
  TOPAZ_BASE_URL: getEnv('TOPAZ_BASE_URL'),
  PAYMENT_SOURCE_BASE_URL: getEnv('PAYMENT_SOURCE_BASE_URL'),
  EVENT_ORDER_STATUS_BASE_URL: getEnv('EVENT_ORDER_STATUS_BASE_URL'),
  RULES_SERVICE_BASE_URL: getEnv('RULES_SERVICE_BASE_URL'),
  VERIFY_SERVICE_BASE_URL: getEnv('VERIFY_SERVICE_BASE_URL'),
  APPLE_GATEWAY_SERVICE_BASE_URL: getEnv('APPLE_GATEWAY_SERVICE_BASE_URL'),
  APPLE_GATEWAY_BASIC_AUTH_TOKEN: getEnv('APPLE_GATEWAY_BASIC_AUTH_TOKEN'),
  CASH_API_BASE_URL: getEnv('CASH_API_BASE_URL'),
  CASH_API_CLIENT_ID: getEnv('CASH_API_CLIENT_ID'),
}

export const app = {
  ADWORDS_ID: getEnv('ADWORDS_ID'),
  AMPLITUDE_CLIENT_SIDE_ID: getEnv('AMPLITUDE_CLIENT_SIDE_ID'),
  APP_ENVIRONMENT: getEnv('APP_ENVIRONMENT'),
  APP_VERSION: getEnv('APP_VERSION'),
  APPLE_PAY_MERCHANT_ID: getEnv('APPLE_PAY_MERCHANT_ID'),
  APPLE_PAY_ACQUIRING_MERCHANT_ID: getEnv('APPLE_PAY_ACQUIRING_MERCHANT_ID'),
  BRAND_NAME: getEnv('BRAND_NAME'),
  BROCHURE_BASE_URL: getEnv('BROCHURE_BASE_URL'),
  FRIENDBUY_MERCHANT_ID: getEnv('FRIENDBUY_MERCHANT_ID'),
  GOOGLEMAPS_API_KEY: getEnv('GOOGLEMAPS_API_KEY'),
  HELP_BASE_URL: getEnv('HELP_BASE_URL'),
  KASADA_SRC: getEnv('KASADA_SRC'),
  LAUNCH_DARKLY_CLIENT_SIDE_ID: getEnv('LAUNCH_DARKLY_CLIENT_SIDE_ID', '5baa19e1a3050e21323b5e58'),
  SNAP_CHAT_PIXEL_ID: getEnv('SNAP_CHAT_PIXEL_ID'),
  TM_ORGANISATION_ID: getEnv('TM_ORGANISATION_ID'),
  THREE_DS_REDIRECT_URL: getEnv('THREE_DS_REDIRECT_URL'),
  STRIPE_CARD_SCAN_PUBLISHABLE_KEY: getEnv('STRIPE_CARD_SCAN_PUBLISHABLE_KEY'),
  BOUNCER_SRC: getEnv('BOUNCER_SRC'),
  DYSCAN_API_KEY: getEnv('DYSCAN_API_KEY'),
  DYSCAN_SRC: getEnv('DYSCAN_SRC'),
  GOOGLE_TAG_MANAGER_ID: getEnv('GOOGLE_TAG_MANAGER_ID'),
  PORTAL_URL: getEnv('PORTAL_URL'),
  DATADOG_APP_ID: getEnv('DATADOG_APP_ID'),
  DATADOG_CLIENT_TOKEN: getEnv('DATADOG_CLIENT_TOKEN'),
  DATADOG_SERVICE: getEnv('DATADOG_SERVICE'),
  PERSONALIZATION_REDIRECTION_DELAY_MS: parseConfig(getEnv('PERSONALIZATION_REDIRECTION_DELAY_MS'), 10000),
  PERSONALIZATION_POSITIVE_ACTIONED_REDIRECTION_DELAY_MS: parseConfig(getEnv('PERSONALIZATION_POSITIVE_ACTIONED_REDIRECTION_DELAY_MS'), 2000),
  PERSONALIZATION_NEGATIVE_ACTIONED_REDIRECTION_DELAY_MS: parseConfig(getEnv('PERSONALIZATION_NEGATIVE_ACTIONED_REDIRECTION_DELAY_MS'), 0),
  PERSONALIZATION_POST_CAPTURE_TIMEOUT: parseConfig(getEnv('PERSONALIZATION_POST_CAPTURE_TIMEOUT'), 8000),
  BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL: parseConfig(getEnv('BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL'), 5000),
  BIOMETRIC_VERIFYING_DEFAULT_WAIT_TIME: parseConfig(getEnv('BIOMETRIC_VERIFYING_DEFAULT_WAIT_TIME'), 90000),
  BIOMETRIC_VERIFYING_TIME_OUT: parseConfig(getEnv('BIOMETRIC_VERIFYING_TIME_OUT'), 300000),
  ENABLE_VERBOSE_NON_PORTAL_API_ERRORS: getEnv('ENABLE_VERBOSE_NON_PORTAL_API_ERRORS', 'false'),
  TOKEN_NOTIFICATION_URL: getEnv('TOKEN_NOTIFICATION_URL'),
  COLD_START_THEME: getEnv('COLD_START_THEME', Theme.AP),
  COLD_START_THEME_US: getEnv('COLD_START_THEME_US') || getEnv('COLD_START_THEME', Theme.AP),
  COLD_START_THEME_CA: getEnv('COLD_START_THEME_CA') || getEnv('COLD_START_THEME', Theme.AP),
  COLD_START_THEME_GB: getEnv('COLD_START_THEME_GB') || getEnv('COLD_START_THEME', Theme.AP),
  COLD_START_THEME_AU: getEnv('COLD_START_THEME_AU') || getEnv('COLD_START_THEME', Theme.AP),
  COLD_START_THEME_NZ: getEnv('COLD_START_THEME_NZ') || getEnv('COLD_START_THEME', Theme.AP),
  MERCHANT_ESTIMATE_PPA_IDS: getEnv('MERCHANT_ESTIMATE_PPA_IDS'),
  CONSUMER_BILLING_SHIPPING_COUNTRY_AS_INITIAL_LANGUAGE: getEnv('CONSUMER_BILLING_SHIPPING_COUNTRY_AS_INITIAL_LANGUAGE'),
  ENABLE_OTP_MERCHANT_ID: getEnv('ENABLE_OTP_MERCHANT_ID'),
  PERSONA_ENV_ID: getEnv('PERSONA_ENV_ID'),
  PERSONA_TEMPLATE_ID: getEnv('PERSONA_TEMPLATE_ID'),
  APPLE_RETAIL_MIDS: getEnv('APPLE_RETAIL_MIDS'),
  AP_COHORT_OFFER_LINK_URL: getEnv('AP_COHORT_OFFER_LINK_URL'),
  CASH_COHORT_OFFER_LINK_URL: getEnv('CASH_COHORT_OFFER_LINK_URL'),
}

export const DEBUG_MODE = getEnv('DEBUG_MODE') === 'true'

export const BASE_PATH = process.env.BASE_PATH
